import { useStoredPreference } from '@cmg/auth';
import { useSearchParams } from '@cmg/common';
import type { GridState, StateUpdatedEvent } from '@cmg/data-grid';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import pick from 'lodash/pick';
import { useCallback, useMemo } from 'react';

const STORED_GRID_STATE_KEYS: (keyof GridState)[] = [
  'sort',
  'filter',
  'columnVisibility',
  'columnOrder',
  'columnPinning',
  'columnSizing',
  'rowGroup',
];

const getStoredGridState = (state: GridState) => {
  const storableState = Object.entries(pick(state, STORED_GRID_STATE_KEYS)).filter(
    ([, value]) => !isNil(value) && !isEmpty(value)
  );

  return Object.fromEntries(storableState);
};

export type Props = Readonly<{
  initialState: GridState | undefined;
  storageKey: string;
}>;

export function useStoredGridState<
  TData,
  TContext extends Record<string, unknown> = Record<string, never>
>({ initialState = {}, storageKey }: Props) {
  const { searchParams, setSearchParams } = useSearchParams();

  /**
   * The grid state is also stored in the browser storage.
   */
  const [storedGridState, setStoredGridState, isStoreGridStateSet, clearStoredGridState] =
    useStoredPreference<GridState>({
      key: storageKey,
      initialValue: initialState,
    });

  /**
   * The grid state is stored in the URL search params.
   * URL search params are updated whenever the grid state is updated.
   */
  const handleStateUpdated = useCallback(
    (event: StateUpdatedEvent<TData, TContext>) => {
      if (!STORED_GRID_STATE_KEYS.some(key => event.sources.includes(key))) {
        return;
      }

      const nextStoredGridState = getStoredGridState(event.state);
      const nextSearchParams = Object.entries(nextStoredGridState).reduce(
        (acc, [key, value]) => ({ ...acc, [key]: JSON.stringify(value) }),
        {}
      );

      setSearchParams(new URLSearchParams(nextSearchParams));
      setStoredGridState(nextStoredGridState);
    },
    [setSearchParams, setStoredGridState]
  );

  const handleClearState = useCallback(() => {
    setSearchParams(new URLSearchParams());

    if (isStoreGridStateSet) {
      clearStoredGridState();
    }
  }, [setSearchParams, isStoreGridStateSet, clearStoredGridState]);

  /**
   * The grid state is stored in the URL and in the browser storage.
   * The URL state takes precedence over the browser storage state.
   */
  const gridState = useMemo(() => {
    const urlState = [...searchParams.entries()]
      .filter(([key]) => STORED_GRID_STATE_KEYS.includes(key as keyof GridState))
      .map(([key, value]) => [key, JSON.parse(value)]);

    if (!isEmpty(urlState)) {
      return Object.fromEntries(urlState);
    }

    return storedGridState;
  }, [storedGridState, searchParams]);

  return {
    gridState,
    handleStateUpdated,
    handleClearState,
  };
}
